import React, { useMemo } from 'react'
import { StyleSheet, Text, View, TouchableOpacity, Platform } from 'react-native'
import { Ionicons, FontAwesome } from '@expo/vector-icons'
import { useTranslation } from 'react-i18next'
import { fontPalette } from '../../../../static/styles/global'

interface QuickActionsCardProps {
  navigation: any
  hasLaborSaver: number | boolean | undefined
  isPTORequestAllowed: boolean | undefined
  hasDefaultAvailability: any
}

const QuickActionsCard = ({
  navigation,
  hasLaborSaver,
  isPTORequestAllowed,
  hasDefaultAvailability
}: QuickActionsCardProps): JSX.Element | null => {
  const { t } = useTranslation()

  // Build dynamic actions list
  const actionsList = useMemo(() => {
    const list = []

    if (isPTORequestAllowed) {
      list.push({
        id: 'pto',
        label: t('Request Time Off'),
        icon: <FontAwesome name="umbrella" size={20} color="#0B495C" />,
        onPress: () => navigation.navigate(Platform.OS === 'web' ? 'EmployeeRequestPTO' : (Platform.OS == 'ios' ? 'EmployeeRequest' : 'EmployeeRequestAndroid')),
        bgColor: '#F0F7F9',
        borderColor: '#E2EEF1',
        textColor: '#0B495C'
      })
    }

    if (hasDefaultAvailability) {
      list.push({
        id: 'availability',
        label: t('Update Availability'),
        icon: <Ionicons name="time" size={22} color="#0B495C" />,
        onPress: () => navigation.navigate('UpdateScheduleAvailability'),
        bgColor: '#F0F7F9',
        borderColor: '#E2EEF1',
        textColor: '#0B495C'
      })
    }

    return list
  }, [navigation, t, hasLaborSaver, isPTORequestAllowed, hasDefaultAvailability])

  if (actionsList.length === 0) {
    return null
  }

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.headerRow}>
        <View style={styles.headerTitleContainer}>
          <View style={styles.iconCircle}>
            <Ionicons name="flash" size={16} color="white" />
          </View>
          <Text style={styles.headerTitle}>{t('Quick Actions')}</Text>
        </View>
      </View>

      {/* Buttons Grid */}
      <View style={styles.gridContainer}>
        {actionsList.map((action) => (
          <TouchableOpacity
            key={action.id}
            style={[
              styles.actionBtn,
              {
                backgroundColor: action.bgColor,
                borderColor: action.borderColor
              }
            ]}
            onPress={action.onPress}
          >
            <View style={styles.iconContainer}>{action.icon}</View>
            <Text style={[styles.btnText, { color: action.textColor }]}>
              {action.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  )
}

export default QuickActionsCard

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap'
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  iconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0B495C',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: fontPalette.primaryBold,
    color: '#0B495C'
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginHorizontal: -4
  },
  actionBtn: {
    width: '23%',
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 4,
    marginHorizontal: '1%',
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
    height: 72
  },
  iconContainer: {
    marginBottom: 6,
    justifyContent: 'center',
    alignItems: 'center'
  },
  btnText: {
    fontSize: 9.5,
    fontFamily: fontPalette.primaryBold,
    textAlign: 'center',
    lineHeight: 12
  }
})
